import React from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";

// Order of the pills, left to right. "warning" sits right after "all" (it's the alert that
// should be seen first) and is rendered ONLY when there is at least one warning route
// (see the filter in render); the other four are always shown.
const ORDER = ["all", "warning", "inactive", "active", "finished"];

// English labels match the live UI (i18n is currently stubbed in this app).
// Dutch for future i18n: Alle / Inactief / Actief / Afgerond / Waarschuwing.
const LABELS = {
  all: "All",
  inactive: "Inactive",
  active: "Active",
  finished: "Finished",
  warning: "Warning"
};

// Soft tint backgrounds used for the selected state of each status pill.
const SELECTED_TINT = {
  inactive: "#EEF0F2",
  active: "#ECEEFF",
  finished: "#E8F2EC", // == theme.palette.success.secondary
  warning: "#FDECEB" // light red, ~ theme.palette.error.secondary
};

// Map each status to an existing theme palette colour.
const statusColor = (theme, key) => {
  switch (key) {
    case "all":
      return theme.palette.darkNavy.main; // #181947
    case "inactive":
      return theme.palette.secondary.main; // #808080
    case "active":
      return theme.palette.primary.main; // #1329FE
    case "finished":
      return theme.palette.success.main; // #07A35A
    case "warning":
      return theme.palette.error.main; // #D93024
    default:
      return theme.palette.secondary.main;
  }
};

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: 5,
    overflowX: "auto",
    padding: theme.spacing(0, 2, 1.5, 3),
    // single non-wrapping row; scrolls sideways if the chips exceed the panel width,
    // with the scrollbar hidden (still scrollable via trackpad / shift-wheel / drag).
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // old Edge/IE
    "&::-webkit-scrollbar": { display: "none" } // Chrome / Safari / Edge
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    flex: "none",
    padding: theme.spacing(0.5, 1),
    border: "1px solid #E2E5EA",
    borderRadius: 16,
    background: "#fff",
    color: "#3C4043",
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.4,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    transition: "background .15s, border-color .15s, color .15s"
  },
  dot: { width: 7, height: 7, borderRadius: "50%", flex: "none" },
  count: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
    borderRadius: 8,
    padding: "0 5px",
    background: "#EEF0F2",
    color: "#5F6368"
  }
}));

/**
 * Route status filter pills for the project "Routes" tab.
 * Presentational only — status computation lives in getRouteListStatus (utils/helpers).
 *
 * @param {string} value     selected key: "all" | "inactive" | "active" | "finished" | "warning"
 * @param {Function} onChange called with the clicked key
 * @param {Object} counts     { all, inactive, active, finished, warning }
 */
export default function RouteStatusFilter({ value = "all", onChange, counts = {} }) {
  const classes = useStyles();
  const theme = useTheme();

  const select = key => () => onChange && onChange(key);

  return (
    <div
      className={classes.wrapper}
      role="tablist"
      aria-label="Filter routes by status"
    >
      {ORDER.filter(
        key => key !== "warning" || (counts.warning || 0) > 0
      ).map(key => {
        const selected = value === key;
        const color = statusColor(theme, key);

        const pillStyle = selected
          ? key === "all"
            ? { background: color, borderColor: color, color: "#fff" }
            : { background: SELECTED_TINT[key], borderColor: color, color }
          : undefined;

        const countStyle = selected
          ? key === "all"
            ? { background: "rgba(255,255,255,0.22)", color: "#fff" }
            : { background: color, color: "#fff" }
          : undefined;

        return (
          <div
            key={key}
            role="tab"
            tabIndex={0}
            aria-selected={selected}
            className={classes.pill}
            style={pillStyle}
            onClick={select(key)}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                select(key)();
              }
            }}
          >
            {key !== "all" && (
              <span className={classes.dot} style={{ background: color }} />
            )}
            <span>{LABELS[key]}</span>
            <span className={classes.count} style={countStyle}>
              {counts[key] || 0}
            </span>
          </div>
        );
      })}
    </div>
  );
}
